import Config from "@/config";
import store from "@/store";
import router from "@/router";
import {contextIdChainFor} from "@/utils/task";

/**
 * Типы панелей. Для нового типа контента достаточно добавить тип сюда
 * и учесть его в panelsFromRoute() и в слоте panel-stack.
 */
export const PANEL_TYPES = {
	CONTEXT: "context",
};

/** Разбирает цепочку id контекстов из params.contextId */
export const parseChain = (contextId) => {
	return contextId?.split(Config.CONTEXT_ID_CHAIN_SEP).filter(el => !!el) || [];
};

/** Роут цепочки контекстов (/c/:contextIdChain) */
export const isChainRoute = (route) => route?.name === "context-chain";

/** Цепочка id контекстов роута: из contextIdChain (context-chain) или из contextId (context) */
export const chainOfRoute = (route) => {
	return parseChain(isChainRoute(route) ? route.params?.contextIdChain : route?.params?.contextId);
};

/**
 * Роут с заданной цепочкой в том же варианте, что и текущий роут (context-chain или context).
 * @param route текущий роут
 * @param chain строка цепочки id
 * @param params дополнительные params
 * @param query query роута
 */
export const routeOfChain = (route, chain, params, query) => {
	return isChainRoute(route)
		? {name: "context-chain", params: {contextIdChain: chain, ...params}, query}
		: {name: "context", params: {contextId: chain, ...params}, query};
};

/**
 * Включён ли панельный режим (group.settings.withPannelView) для цепочки контекстов:
 * проект определяем по первому id цепочки.
 * @param contextId цепочка id или её первый элемент
 */
export const isPannelViewOfContextId = (contextId) => {
	const rootId = parseChain(contextId)[0];
	if (!rootId) return false;
	const group = store.state.session.area?.groups?.find(el => el.id === rootId || el.contextId === rootId);
	return !!group?.settings?.withPannelView;
};

/**
 * Строит список панелей по текущему роуту.
 * contextId - цепочка id через Config.CONTEXT_ID_CHAIN_SEP: число id равно числу панелей, порядок id - порядок панелей,
 * каждая следующая панель - потомок левой соседней. Устаревший роут context-task (контекст + задача)
 * трактуется как цепочка с задачей в конце.
 * @param route текущий роут (this.$route)
 * @returns {Array<{key: string, type: string, id: string, contextId: string}>}
 */
export const panelsFromRoute = (route) => {
	const chain = chainOfRoute(route);
	if (route?.name === "context-task" && route.params.taskId) chain.push(route.params.taskId);
	return chain.map((id, index) => ({
		key: "context:" + id,
		type: PANEL_TYPES.CONTEXT,
		id,
		// цепочка до этой панели включительно - id контекста для запроса данных
		contextId: chain.slice(0, index + 1).join(Config.CONTEXT_ID_CHAIN_SEP),
	}));
};

/**
 * Маршрут "открыть элемент в новой панели справа": всегда чистый роут context, к цепочке добавляется id элемента.
 * Панели правее панели, в списке которой находится элемент (parent), отбрасываются.
 * @param item задача/папка, которую открываем (даже если это лист)
 * @param parent контекст, в списке которого лежит элемент; по умолчанию - текущий контекст
 */
export const routeOfContextAppend = (item, parent) => {
	if (!item) return null;
	const sep = Config.CONTEXT_ID_CHAIN_SEP;
	// процесс открывается сразу двумя панелями: сам процесс и его актуальный шаг (task.processStepId,
	// на клиенте он приходит как firstAssignableChild.taskId)
	const stepId = item.isProcess ? item.firstAssignableChild?.taskId : null;
	const stepIds = stepId && stepId !== item.id && stepId !== item.auth ? [stepId] : [];
	const currentChain = chainOfRoute(router.currentRoute);
	const parentId = parent?.auth || parent?.id;
	const index = parentId ? currentChain.indexOf(parentId) : -1;
	let chain;
	if (index >= 0) chain = [...currentChain.slice(0, index + 1), item.auth || item.id, ...stepIds].join(sep);
	else chain = [contextIdChainFor(item, undefined, parent), ...stepIds].join(sep);	// элемент вложенного узла списка: цепочка по родителям
	return routeOfChain(router.currentRoute, chain, undefined, {...router.currentRoute.query});
};

/**
 * Роут для закрытия панели: цепочка обрезается до панели слева.
 * @param panels список панелей
 * @param index индекс закрываемой панели
 * @param route текущий роут
 * @returns {object|null}
 */
export const routeToClosePanel = (panels, index, route) => {
	const parent = panels[index - 1];
	if (!parent) return null;
	return routeOfChain(route, parent.contextId, {skipContext: true}, route?.query);
};
